/**
 * Public email providers. A tenant's allowed domains must be corporate, so these are
 * rejected on entry and on sign-in.
 */
export const PUBLIC_EMAIL_DOMAINS: ReadonlySet<string> = new Set([
  'gmail.com',
  'googlemail.com',
  'hotmail.com',
  'hotmail.es',
  'hotmail.com.ar',
  'outlook.com',
  'outlook.es',
  'live.com',
  'msn.com',
  'yahoo.com',
  'yahoo.com.ar',
  'yahoo.es',
  'ymail.com',
  'icloud.com',
  'me.com',
  'mac.com',
  'aol.com',
  'proton.me',
  'protonmail.com',
  'pm.me',
  'gmx.com',
  'gmx.net',
  'zoho.com',
  'yandex.com',
  'mail.com',
  'fastmail.com',
  'hey.com',
  'tutanota.com',
  'duck.com',
]);

const DOMAIN_RE = /^(?=.{4,253}$)([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/;

export type DomainValidation =
  | { ok: true; domain: string }
  | { ok: false; reason: 'empty' | 'invalid' | 'public'; message: string };

/** Normalises and validates a corporate domain. Pure; the MX check lives in the API. */
export function validateCorporateDomain(input: string): DomainValidation {
  const domain = input.trim().toLowerCase().replace(/^@/, '');
  if (domain.length === 0) {
    return { ok: false, reason: 'empty', message: 'Enter a domain.' };
  }
  if (!DOMAIN_RE.test(domain)) {
    return { ok: false, reason: 'invalid', message: 'Enter a domain such as company.com.' };
  }
  if (PUBLIC_EMAIL_DOMAINS.has(domain)) {
    return {
      ok: false,
      reason: 'public',
      message: 'Personal email providers are not allowed. Use the corporate domain.',
    };
  }
  return { ok: true, domain };
}

export function emailDomain(email: string): string {
  const at = email.lastIndexOf('@');
  return at === -1 ? '' : email.slice(at + 1).trim().toLowerCase();
}

export function normaliseEmail(email: string): string {
  return email.trim().toLowerCase();
}

/** True when the email's domain is one of the tenant's allowed domains. */
export function isEmailAllowed(email: string, allowedDomains: readonly string[]): boolean {
  const domain = emailDomain(email);
  return domain.length > 0 && allowedDomains.some((d) => d.toLowerCase() === domain);
}
